// packages/api/src/PublicQrTagService.ts
//
// ROADMAP #172 phase 2 (owner, 2026-10-19) — PUBLIC QR TAGS. Mirrors eventsmagic-api QrTagCore's public actions:
//   page        — `publicQrTag`: what anyone's phone camera sees at `<site>/t/<code>` — the host's title and message, and
//                 ONLY the holder details the holder agreed to. `{ found: false }` for everything not shown.
//   mine        — `publicQrTagsMine`: the public tags held by the party this browser answers for (the RSVP's panel).
//   consentSheet — `publicQrTagConsentSheet`: the tag an emailed link (`/my-tag/<token>`) names. A READ.
//   setConsent  — `publicQrTagConsent`: the holder chooses what the page shows ([] withdraws everything); by the emailed
//                 link's token, or by url + visitorId.
//
// Runs through the injected client (see `configureApiService`) so it works in the BROWSER (the proxy forwards the
// visitor's address — the reads are rate-limited per visitor).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import type { PublicEventOrganizer, PublicEventTheme } from "./PublicEventService";

/** One holder detail: on the page, a shown one; on the holder's reads, every offered one with `agreed`. */
export interface PublicQrTagDetail {
  /** 'first_name' | 'full_name' | 'phone' | 'q:<question id>' */
  key: string;
  label: string;
  /** Null when this reader may not see it (an unproven browser sees only the holder's name). */
  value: string | null;
  agreed: boolean;
}

export interface PublicQrTag {
  found: boolean;
  /** The holder's reads only. */
  id?: string | null;
  code?: string | null;
  sequenceNo?: number | null;
  label?: string | null;
  title?: string | null;
  message?: string | null;
  isPublic?: boolean | null;
  eventTitle?: string | null;
  /** The event website's theme — the tag's pages wear it (null = the default look). */
  theme?: PublicEventTheme | null;
  /** The event's organizer credit — its logo heads the page (null for none / a draft profile). */
  organizer?: Pick<PublicEventOrganizer, "id" | "slug" | "name" | "logoUploadedFile"> | null;
  holderName?: string | null;
  publicUrl?: string | null;
  /** The holder's reads: the policy they accepted when registering — every offered detail shows, no switches. */
  agreedByPolicy?: string | null;
  agreedAt?: string | null;
  /** The event's zone — dates on the holder's pages are on the event's calendar. */
  eventTimezone?: string | null;
  details?: PublicQrTagDetail[] | null;
}

const TAG_FIELDS = `
  found
  id
  code
  sequenceNo
  label
  title
  message
  isPublic
  eventTitle
  theme { id name mode tokens customCss }
  organizer { id slug name logoUploadedFile { fileUrl thumbnailUrl } }
  holderName
  publicUrl
  agreedByPolicy
  agreedAt
  eventTimezone
  details { key label value agreed }
`;

const PAGE_QUERY = gql`
  query PublicQrTag($params: PublicQrTagInput) {
    publicQrTag(params: $params) {
      code
      errors { name errors }
      data { ${TAG_FIELDS} }
    }
  }
`;

const MINE_QUERY = gql`
  query PublicQrTagsMine($params: PublicQrTagsMineInput) {
    publicQrTagsMine(params: $params) {
      code
      errors { name errors }
      data { ${TAG_FIELDS} }
    }
  }
`;

const SHEET_QUERY = gql`
  query PublicQrTagConsentSheet($params: PublicQrTagConsentSheetInput) {
    publicQrTagConsentSheet(params: $params) {
      code
      errors { name errors }
      data { ${TAG_FIELDS} }
    }
  }
`;

const CONSENT_MUTATION = gql`
  mutation PublicQrTagConsent($params: PublicQrTagConsentInput) {
    publicQrTagConsent(params: $params) {
      code
      errors { name errors }
      data { ${TAG_FIELDS} }
    }
  }
`;

export interface PublicQrTagAskOrganizerInput {
  qrTagId: string;
  /** The holder's own words (optional, at most 1000 characters). */
  note?: string | null;
  /** The emailed link's token — or url + visitorId. */
  token?: string | null;
  url?: string | null;
  visitorId?: string | null;
  eventTimeSlotId?: string | null;
}

const ASK_ORGANIZER_MUTATION = gql`
  mutation PublicQrTagAskOrganizer($params: PublicQrTagAskOrganizerInput) {
    publicQrTagAskOrganizer(params: $params) {
      code
      errors { name errors }
      data { ok }
    }
  }
`;

export interface PublicQrTagConsentInput {
  qrTagId: string;
  /** What the page shows — offered keys; [] shows nothing of the holder. */
  fields: string[];
  /** The emailed link's token — or url + visitorId. */
  token?: string | null;
  url?: string | null;
  visitorId?: string | null;
  eventTimeSlotId?: string | null;
}

export class PublicQrTagService {
  /** The public page of a tag code. */
  static async page(code: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(PAGE_QUERY, { params: { code } }, { fetchPolicy: "network-only", ...options });
  }

  /** The public tags held by the party this browser answers for. */
  static async mine(url: string, visitorId: string, eventTimeSlotId?: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(MINE_QUERY, { params: { url, visitorId, eventTimeSlotId: eventTimeSlotId || null } }, { fetchPolicy: "network-only", ...options });
  }

  /** The tag an emailed link names — a read; nothing changes until `setConsent`. */
  static async consentSheet(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(SHEET_QUERY, { params: { token } }, { fetchPolicy: "network-only", ...options });
  }

  /** The holder asks the organizer to stop showing their details (to the event's inbox). */
  static async askOrganizer(input: PublicQrTagAskOrganizerInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(ASK_ORGANIZER_MUTATION, { params: input }, options);
  }

  /** The holder chooses what the page shows. */
  static async setConsent(input: PublicQrTagConsentInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(CONSENT_MUTATION, { params: input }, options);
  }
}
